import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import { getItem } from '../../../../../utils/localStorage';

function ExpenseTab() {
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const [comments, setComments] = useState('');
    const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
    const [approvalAction, setApprovalAction] = useState(null);
    const [selectedExpenseId, setSelectedExpenseId] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [expenseType, setExpenseType] = useState('ONE_TIME');
    const [rows, setRows] = useState(10);
    const [expenseCategory, setExpenseCategory] = useState('');
    const [expenseBudget, setExpenseBudget] = useState('');
    const [dates, setDates] = useState([]);
    const [searchText, setSearchText] = useState('');
    
    // New state for Head Management/VAS selection
    const [managementType, setManagementType] = useState('HEAD_MANAGEMENT');
    const [selectedUnit, setSelectedUnit] = useState('');
    const [unitSearchText, setUnitSearchText] = useState('');
    const [units, setUnits] = useState([]);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [showUnitDropdown, setShowUnitDropdown] = useState(false); // Add this state
    
    const [expenseData, setExpenseData] = useState({
        name: '',
        amount: '',
        description: '',
        date: ''
    });

    // Pagination options
    const rowsPerPageOptions = [10, 20, 50, 100];

    // Data state
    const [categories, setCategories] = useState([]);
    const [budgets, setBudgets] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getItem("APPUSER").then(userData => {
            if (typeof userData === 'string') userData = JSON.parse(userData);
            setCurrentUser(userData?.user || null);
        });
    }, []);

    useEffect(() => {
        if (selectedFacilityId) {
            fetchExpenses();
            fetchCategories();
            fetchBudgets();
        }
    }, [selectedFacilityId]);

    // Fetch units when VAS is selected
    useEffect(() => {
        if (managementType === 'VAS' && selectedFacilityId) {
            fetchUnits();
        }
    }, [managementType, selectedFacilityId]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await makeRequest2(`/api/app/expense_management/expenses/get_all/${selectedFacilityId}`, 'GET');
            setExpenses(res?.data?.expenses || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        const res = await makeRequest2(`/api/app/expense_management/expense_category/get_all_expense_categories/${selectedFacilityId}`, 'GET');
        setCategories(res?.data?.data || []);
    };

    const fetchBudgets = async () => {
        const res = await makeRequest2(`/api/app/expense_management/get_budgets/${selectedFacilityId}`, 'GET');
        setBudgets(res?.data?.budgets || []);
    };

    const fetchUnits = async () => {
        setLoadingUnits(true);
        try {
            // Using the analog meter endpoint pattern
            const res = await makeRequest2(`/api/app/unit_management/get_facility_units/${selectedFacilityId}`, 'GET');
            setUnits(res?.data?.units || []);
        } catch (err) {
            console.error('Error fetching units:', err);
            setUnits([]);
        } finally {
            setLoadingUnits(false);
        }
    };

    const isUserApprover = (expense) => {
        if (!currentUser?.userId || !Array.isArray(expense.approvals)) return false;
        const step = expense.approvals.find(s => s.stepNumber === expense.currentStep);
        return step?.approvers?.some(approver =>
            String(approver.userId) === currentUser.userId &&
            approver.status === 'pending'
        );
    };

    const hasUserApproved = (expense) => {
        if (!currentUser?.userId || !expense.approvals) return false;
        return expense.approvals.some(step =>
            step.approvers?.some(approver =>
                String(approver.userId) === currentUser.userId &&
                approver.status === 'approved'
            )
        );
    };

    const openCommentsDialog = (id, action) => {
        setSelectedExpenseId(id);
        setApprovalAction(action);
        setComments('');
        setCommentsDialogVisible(true);
    };

    const submitApprovalAction = async () => {
        if (!selectedExpenseId || !approvalAction) return;
        setLoading(true);
        setCommentsDialogVisible(false);
        try {
            const endpoint = `/api/app/expense_management/expenses/approve/${selectedFacilityId}/${selectedExpenseId}`;
            const approve = approvalAction === 'approve';
            const response = await makeRequest2(endpoint, 'PUT', {
                approve,
                comments,
                userId: currentUser.userId
            });
            if (response.success) {
                toastify(`Expense ${approve ? 'approved' : 'rejected'} successfully`, 'success');
                fetchExpenses();
            } else {
                toastify(response.error || 'Action failed', 'error');
            }
        } catch (error) {
            toastify('Error submitting action', 'error');
        } finally {
            setLoading(false);
            setSelectedExpenseId(null);
            setApprovalAction(null);
        }
    };

    const actionTemplate = (rowData) => {
        const isApprover = isUserApprover(rowData);
        const hasApproved = hasUserApproved(rowData);
        const isPending = ['pending', 'in_progress'].includes((rowData.approvalStatus || '').toLowerCase());

        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    // onClick={() => handleViewDetails(rowData._id)}
                    tooltip="View"
                />
                <Button
                    icon="ti ti-edit"
                    className="p-button-rounded p-button-warning"
                    // onClick={() => handleEditRequest(rowData._id)}
                    tooltip="Edit"
                    disabled={(rowData.status || '').toLowerCase() !== 'pending'}
                />
                {isApprover && isPending && !hasApproved && (
                    <>
                        <Button
                            icon="ti ti-check"
                            className="p-button-rounded p-button-success"
                            tooltip="Approve"
                            onClick={() => openCommentsDialog(rowData._id, 'approve')}
                        />
                        <Button
                            icon="ti ti-x"
                            className="p-button-rounded p-button-danger"
                            tooltip="Reject"
                            onClick={() => openCommentsDialog(rowData._id, 'reject')}
                        />
                    </>
                )}
            </div>
        );
    };

    const commentsDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setCommentsDialogVisible(false)} />
            <Button
                label={approvalAction === 'approve' ? 'Approve' : 'Reject'}
                icon={approvalAction === 'approve' ? 'pi pi-check' : 'pi pi-times'}
                className={approvalAction === 'approve' ? 'p-button-success' : 'p-button-danger'}
                onClick={submitApprovalAction}
            />
        </div>
    );

    const handleExpenseTypeChange = (e) => {
        setExpenseType(e.target.value);
        if (e.target.value === 'ONE_TIME') {
            setDates([]);
        }
    };

    const handleExpenseCategoryChange = (e) => {
        setExpenseCategory(e.target.value);
    };

    const handleExpenseBudgetChange = (e) => {
        setExpenseBudget(e.target.value);
    };

    const handleManagementTypeChange = (e) => {
        setManagementType(e.target.value);
        // Reset unit selection when switching types
        if (e.target.value === 'HEAD_MANAGEMENT') {
            setSelectedUnit('');
            setUnitSearchText('');
            setShowUnitDropdown(false);
        }
    };

    // Handle unit search input change
    const handleUnitSearchChange = (e) => {
        setUnitSearchText(e.target.value);
        setShowUnitDropdown(true); // Show dropdown when typing
    };

    // Handle unit selection
    const handleUnitSelect = (unit) => {
        setSelectedUnit(unit._id);
        setUnitSearchText(unit.name || unit.unitName || '');
        setShowUnitDropdown(false); // Hide dropdown after selection
    };

    const addDate = () => {
        setDates([...dates, '']);
    };

    const updateDate = (index, value) => {
        const newDates = [...dates];
        newDates[index] = value;
        setDates(newDates);
    };

    const removeDate = (index) => {
        const newDates = dates.filter((_, i) => i !== index);
        setDates(newDates);
    };

    const handleInputChange = (field, value) => {
        setExpenseData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        if (!expenseData.name.trim()) {
            toastify('Please enter expense name', 'error');
            return false;
        }
        if (!expenseData.amount || expenseData.amount <= 0) {
            toastify('Please enter a valid amount', 'error');
            return false;
        }
        if (!expenseCategory) {
            toastify('Please select a category', 'error');
            return false;
        }
        if (!expenseBudget) {
            toastify('Please select a budget', 'error');
            return false;
        }
        if (managementType === 'VAS' && !selectedUnit) {
            toastify('Please select a unit for VAS expenses', 'error');
            return false;
        }
        if (expenseType === 'ONE_TIME' && !expenseData.date) {
            toastify('Please select a date', 'error');
            return false;
        }
        if (expenseType === 'RECURRING' && dates.length === 0) {
            toastify('Please add at least one recurring date', 'error');
            return false;
        }
        if (expenseType === 'RECURRING' && dates.some(date => !date)) {
            toastify('Please fill all recurring dates', 'error');
            return false;
        }
        return true;
    };

    const handleSaveExpense = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            const expensePayload = {
                facilityId: selectedFacilityId,
                name: expenseData.name.trim(),
                amount: parseFloat(expenseData.amount),
                description: expenseData.description.trim(),
                type: expenseType,
                categoryId: expenseCategory,
                budgetId: expenseBudget,
                managementType: managementType,
                unitId: managementType === 'VAS' ? selectedUnit : null,
                date: expenseType === 'ONE_TIME' ? expenseData.date : null,
                dates: expenseType === 'RECURRING' ? dates : null
            };

            const response = await makeRequest2(
                `/api/app/expense_management/expenses/add_expense/${selectedFacilityId}`,
                'POST',
                expensePayload
            );

            if (response.success) {
                toastify(response.message || 'Expense added successfully', 'success');

                // Reset form
                setExpenseData({
                    name: '',
                    amount: '',
                    description: '',
                    date: ''
                });
                setDates([]);
                setExpenseType('ONE_TIME');
                setExpenseCategory('');
                setExpenseBudget('');
                setManagementType('HEAD_MANAGEMENT');
                setSelectedUnit('');
                setUnitSearchText('');
                setShowUnitDropdown(false); // Reset dropdown state

                // Close modal
                const modalElement = document.getElementById('addExpenseModal');
                const modal = window.bootstrap?.Modal?.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }

                // Refresh expenses list
                fetchExpenses();
            } else {
                toastify(response.error || 'Failed to add expense', 'error');
            }
        } catch (error) {
            console.error('Error saving expense:', error);
            toastify('Error saving expense', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Filter expenses based on search
    const filteredExpenses = expenses.filter(expense =>
        expense.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        expense.description?.toLowerCase().includes(searchText.toLowerCase())
    );

    // Filter units based on search text
    const filteredUnits = units.filter(unit =>
        unit.name?.toLowerCase().includes(unitSearchText.toLowerCase()) ||
        unit.unitName?.toLowerCase().includes(unitSearchText.toLowerCase())
    );

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return 'Invalid Date';
        }
    };

    const getCategoryName = (expense) => {
        if (expense.CategoryInfo && expense.CategoryInfo.name) {
            return expense.CategoryInfo.name;
        }

        const category = categories.find(cat => cat._id === expense.categoryId);
        return category?.name || 'Unknown';
    };

    const getBudgetName = (expense) => {
        if (expense.BudgetInfo && expense.BudgetInfo.name) {
            return expense.BudgetInfo.name;
        }

        const budget = budgets.find(bud => bud._id === expense.budgetId);
        return budget?.name || budget?.budgetName || 'Unknown';
    };

    if (!selectedFacilityId) {
        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body text-center">
                            Please select a facility to manage expenses.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col-lg-12 col-xxl-12">
                <div className="card">
                    <div className="card-body position-relative">
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addExpenseModal"
                                >
                                    Add New Expense
                                </button>
                            </div>
                            <div className="col-md-3 ms-auto">
                                <input
                                    className="form-control"
                                    placeholder="Search expenses..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Add Expense Modal */}
                        <div
                            className="modal fade"
                            id="addExpenseModal"
                            tabIndex="-1"
                            aria-labelledby="addExpenseModalLabel"
                            aria-hidden="true"
                        >
                            <div className="modal-dialog modal-lg">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5
                                            className="modal-title"
                                            id="addExpenseModalLabel"
                                        >
                                            Add New Expense
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            data-bs-dismiss="modal"
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <form>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Expense Name <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={expenseData.name}
                                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                                            placeholder="Enter expense name"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Amount <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={expenseData.amount}
                                                            onChange={(e) => handleInputChange('amount', e.target.value)}
                                                            placeholder="Enter amount"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Management Type Selection */}
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Management Type <span className="text-danger">*</span>
                                                        </label>
                                                        <select
                                                            className="form-select"
                                                            value={managementType}
                                                            onChange={handleManagementTypeChange}
                                                        >
                                                            <option value="HEAD_MANAGEMENT">Head Management</option>
                                                            <option value="VAS">VAS</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                
                                                {/* Unit Selection - Only visible for VAS */}
                                                {managementType === 'VAS' && (
                                                    <div className="col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label">
                                                                Unit <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="position-relative">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search and select unit..."
                                                                    value={unitSearchText}
                                                                    onChange={handleUnitSearchChange}
                                                                    onFocus={() => setShowUnitDropdown(true)}
                                                                />
                                                                {showUnitDropdown && unitSearchText && filteredUnits.length > 0 && (
                                                                    <div className="dropdown-menu show w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                        {filteredUnits.map((unit) => (
                                                                            <button
                                                                                key={unit._id}
                                                                                type="button"
                                                                                className="dropdown-item"
                                                                                onClick={() => handleUnitSelect(unit)}
                                                                            >
                                                                                {unit.name || unit.unitName}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {loadingUnits && (
                                                                    <div className="text-center p-2">
                                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                        <span className="ms-2">Loading units...</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Category <span className="text-danger">*</span>
                                                        </label>
                                                        <select
                                                            className="form-select"
                                                            value={expenseCategory}
                                                            onChange={handleExpenseCategoryChange}
                                                        >
                                                            <option value="">Select Category</option>
                                                            {categories.map((category) => (
                                                                <option key={category._id} value={category._id}>
                                                                    {category.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Budget <span className="text-danger">*</span>
                                                        </label>
                                                        <select
                                                            className="form-select"
                                                            value={expenseBudget}
                                                            onChange={handleExpenseBudgetChange}
                                                        >
                                                            <option value="">Select Budget</option>
                                                            {budgets.map((budget) => (
                                                                <option key={budget._id} value={budget._id}>
                                                                    {budget.budgetName} - {formatCurrency(budget.allocation)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Type <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-select"
                                                    value={expenseType}
                                                    onChange={handleExpenseTypeChange}
                                                >
                                                    <option value="ONE_TIME">One Time</option>
                                                    <option value="RECURRING">Recurring</option>
                                                </select>
                                            </div>

                                            {expenseType === 'ONE_TIME' ? (
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Date <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={expenseData.date}
                                                        onChange={(e) => handleInputChange('date', e.target.value)}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="mb-3">
                                                    <label className="form-label mb-3">
                                                        Recurring Dates <span className="text-danger">*</span>
                                                    </label>
                                                    <div className="d-flex flex-column gap-3">
                                                        {dates.map((date, index) => (
                                                            <div
                                                                key={index}
                                                                className="d-flex align-items-center gap-3"
                                                            >
                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    value={date}
                                                                    onChange={(e) =>
                                                                        updateDate(index, e.target.value)
                                                                    }
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeDate(index)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary w-auto"
                                                            onClick={addDate}
                                                        >
                                                            Add Date
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Description
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={expenseData.description}
                                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                                    placeholder="Enter expense description"
                                                ></textarea>
                                            </div>
                                        </form>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            data-bs-dismiss="modal"
                                        >
                                            Close
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleSaveExpense}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Expense'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Expenses Table */}
                        <div className="dt-responsive table-responsive">
                            <DataTable
                                value={filteredExpenses}
                                paginator
                                rows={rows}
                                rowsPerPageOptions={rowsPerPageOptions}
                                stripedRows
                                loading={loading}
                                emptyMessage="No expenses found"
                            >
                                <Column field="name" header="Name" sortable />
                                <Column field="amount" header="Amount" body={(rowData) => formatCurrency(rowData.amount)} />
                                <Column field="type" header="Type" body={(rowData) => rowData.type?.replace('_', ' ')} />
                                <Column field="managementType" header="Management" body={(rowData) => rowData.managementType?.replace('_', ' ')} />
                                <Column field="categoryId" header="Category" body={(rowData) => getCategoryName(rowData)} />
                                <Column field="budgetId" header="Budget" body={(rowData) => getBudgetName(rowData)} />
                                <Column field="date" header="Date" body={(rowData) => formatDate(rowData.date)} />
                                <Column field="status" header="Status" body={(rowData) => (
                                    <span className={`badge ${rowData.status === 'PAID' ? 'bg-success' : 'bg-warning'}`}>{rowData.status}</span>
                                )} />
                                <Column field="approvalStatus" header="Approval" body={(rowData) => (
                                    <span className={`badge ${rowData.approvalStatus === 'approved' ? 'bg-success' :
                                        rowData.approvalStatus === 'rejected' ? 'bg-danger' :
                                            rowData.approvalStatus === 'in_progress' ? 'bg-info' : 'bg-secondary'
                                        }`}>
                                        {rowData.approvalStatus?.replace('_', ' ').toUpperCase()}
                                    </span>
                                )} />
                                <Column header="ACTIONS" body={actionTemplate} style={{ width: '12rem' }} />
                            </DataTable>

                            <Dialog
                                header={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} Expense`}
                                visible={commentsDialogVisible}
                                style={{ width: '500px' }}
                                footer={commentsDialogFooter}
                                onHide={() => setCommentsDialogVisible(false)}
                            >
                                <div className="field">
                                    <label htmlFor="comments">Comments (Optional)</label>
                                    <InputTextarea
                                        id="comments"
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                        rows={5}
                                        cols={30}
                                        className="w-100"
                                        placeholder="Enter any comments regarding your decision..."
                                    />
                                </div>
                            </Dialog>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ExpenseTab;